import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useForm, useStore } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { Description, ErrorMessage, Field, Fieldset, Label, Legend } from "src/components/base/fieldset";
import { Radio, RadioField, RadioGroup } from "src/components/base/radio";
import { Input } from "src/components/base/input";
import { Text } from "src/components/base/text";
import { Button, PrimaryButton } from "src/components/base/button";
import { Divider } from "src/components/base/divider";
import { ApiInternal } from "src/api/api_internal";
import { notify } from "src/components/base/toast";
import { GlobalDnsType } from "src/api/generated/frontend_internal_v1";

/**
 * Public-resolver IPs per provider. Shown only as a caption under each radio.
 */
type ProviderResolverPreset = {
    /** Up to two IPv4 anycast addresses. */
    ipv4: string[];
    /** Up to two IPv6 anycast addresses. */
    ipv6: string[];
};

/**
 * Well-known IPs for each preset DNS provider. Shown as a caption under each radio so the
 * admin sees exactly what they're selecting without having to switch to "Custom" first.
 * Tailscale's client treats all configured resolvers as a parallel race — there's no hard
 * limit, but ~2 IPv4 + ~2 IPv6 per provider is the sweet spot.
 */
const PROVIDER_PRESETS: Record<Exclude<GlobalDnsType, "Custom">, ProviderResolverPreset> = {
    Google: { ipv4: ["8.8.8.8", "8.8.4.4"], ipv6: ["2001:4860:4860::8888", "2001:4860:4860::8844"] },
    Cloudflare: {
        ipv4: ["1.1.1.1", "1.0.0.1"],
        ipv6: ["2606:4700:4700::1111", "2606:4700:4700::1001"],
    },
    Quad9: { ipv4: ["9.9.9.9", "149.112.112.112"], ipv6: ["2620:fe::fe", "2620:fe::9"] },
};

const IPV4_FIELDS = ["ipv4provider1", "ipv4provider2", "ipv4provider3"] as const;
const IPV6_FIELDS = ["ipv6provider1", "ipv6provider2", "ipv6provider3"] as const;

/**
 * An overview of all Global DNS.
 *
 * @returns the rendered route component
 */
export default function GlobalDnsOverview() {
    const [t] = useTranslation("admin-dns");
    const [tg] = useTranslation();

    const router = useRouter();
    const data = Route.useLoaderData();

    const form = useForm({
        defaultValues: {
            dns: data.dns_type,
            ipv4provider1: data.ipv4.at(0) ?? "",
            ipv4provider2: data.ipv4.at(1) ?? "",
            ipv4provider3: data.ipv4.at(2) ?? "",
            ipv6provider1: data.ipv6.at(0) ?? "",
            ipv6provider2: data.ipv6.at(1) ?? "",
            ipv6provider3: data.ipv6.at(2) ?? "",
        },
        validators: {
            onSubmitAsync: async ({ value }) => {
                const ipv4 = IPV4_FIELDS.map((n) => value[n]).filter((v) => v !== "");
                const ipv6 = IPV6_FIELDS.map((n) => value[n]).filter((v) => v !== "");

                if (value.dns === "Custom" && ipv4.length === 0 && ipv6.length === 0) {
                    return {
                        fields: {
                            ipv4provider1: t("error.one-ip-should-provided"),
                            ipv6provider1: t("error.one-ip-should-provided"),
                        },
                    };
                }

                const res = await ApiInternal.dns.update({ dns_type: value.dns, ipv4, ipv6 });
                if (res.result === "Err") {
                    return {
                        fields: {
                            ipv4provider1: res.error.ipv4_provider1
                                ? t("error.ipv4-invalid")
                                : res.error.missing_ip_address
                                  ? t("error.one-ip-should-provided")
                                  : undefined,
                            ipv4provider2: res.error.ipv4_provider2 ? t("error.ipv4-invalid") : undefined,
                            ipv4provider3: res.error.ipv4_provider3 ? t("error.ipv4-invalid") : undefined,
                            ipv6provider1: res.error.ipv6_provider1
                                ? t("error.ipv6-invalid")
                                : res.error.missing_ip_address
                                  ? t("error.one-ip-should-provided")
                                  : undefined,
                            ipv6provider2: res.error.ipv6_provider2 ? t("error.ipv6-invalid") : undefined,
                            ipv6provider3: res.error.ipv6_provider3 ? t("error.ipv6-invalid") : undefined,
                        },
                    };
                }
            },
        },
        onSubmit: () => {
            notify.success(t("toast.success"));
            router.invalidate({ sync: true });
        },
    });

    const dnsType = useStore(form.store, (s) => s.values.dns);
    const isDirty = useStore(form.store, (s) => s.isDirty);
    const isCustom = dnsType === GlobalDnsType.Custom;

    return (
        <Form className={"flex max-w-2xl flex-col gap-8"} onSubmit={form.handleSubmit}>
            <Fieldset>
                <Legend>{t("heading.global-dns-legend")}</Legend>
                <Text>{t("description.global-dns")}</Text>
                <form.Field name={"dns"}>
                    {(fieldApi) => (
                        <RadioGroup
                            name={"dns"}
                            value={fieldApi.state.value}
                            onChange={(e) => fieldApi.handleChange(e as GlobalDnsType)}
                            className={"mt-4"}
                        >
                            <PresetRadio
                                value={GlobalDnsType.Google}
                                label={t("label.google")}
                                preset={PROVIDER_PRESETS.Google}
                            />
                            <PresetRadio
                                value={GlobalDnsType.Cloudflare}
                                label={t("label.cloudflare")}
                                preset={PROVIDER_PRESETS.Cloudflare}
                            />
                            <PresetRadio
                                value={GlobalDnsType.Quad9}
                                label={t("label.quad9")}
                                preset={PROVIDER_PRESETS.Quad9}
                            />
                            <RadioField>
                                <Radio value={GlobalDnsType.Custom} />
                                <Label>{t("label.custom")}</Label>
                                <Description>{t("description.custom-hint")}</Description>
                            </RadioField>
                        </RadioGroup>
                    )}
                </form.Field>
            </Fieldset>

            {isCustom && (
                <>
                    <Divider />
                    <div className={"grid grid-cols-1 gap-8 sm:grid-cols-2"}>
                        <Fieldset>
                            <Legend>{t("heading.custom-dns-global-ipv4")}</Legend>
                            <Text>{t("description.custom-dns-global-ipv4-short")}</Text>
                            <div className={"mt-4 flex flex-col gap-3"}>
                                {IPV4_FIELDS.map((name, idx) => (
                                    <form.Field
                                        key={name}
                                        name={name}
                                        validators={
                                            idx === 0
                                                ? {
                                                      onChangeListenTo: [
                                                          "dns",
                                                          ...IPV4_FIELDS.slice(1),
                                                          ...IPV6_FIELDS,
                                                      ],
                                                      onChange: ({ value, fieldApi }) => {
                                                          if (
                                                              fieldApi.form.getFieldValue("dns") !==
                                                              GlobalDnsType.Custom
                                                          )
                                                              return undefined;
                                                          const allEmpty = [
                                                              value,
                                                              ...IPV4_FIELDS.slice(1).map((n) =>
                                                                  fieldApi.form.getFieldValue(n),
                                                              ),
                                                              ...IPV6_FIELDS.map((n) => fieldApi.form.getFieldValue(n)),
                                                          ].every((v) => v === "");
                                                          return allEmpty
                                                              ? t("error.one-ip-should-provided")
                                                              : undefined;
                                                      },
                                                  }
                                                : undefined
                                        }
                                    >
                                        {(fieldApi) => (
                                            <Field>
                                                <Input
                                                    placeholder={"192.0.2.1"}
                                                    value={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                    invalid={fieldApi.state.meta.errors.length > 0}
                                                />
                                                {fieldApi.state.meta.errors.map((err) => (
                                                    <ErrorMessage key={err}>{err}</ErrorMessage>
                                                ))}
                                            </Field>
                                        )}
                                    </form.Field>
                                ))}
                            </div>
                        </Fieldset>

                        <Fieldset>
                            <Legend>{t("heading.custom-dns-global-ipv6")}</Legend>
                            <Text>{t("description.custom-dns-global-ipv6-short")}</Text>
                            <div className={"mt-4 flex flex-col gap-3"}>
                                {IPV6_FIELDS.map((name, idx) => (
                                    <form.Field
                                        key={name}
                                        name={name}
                                        validators={
                                            idx === 0
                                                ? {
                                                      onChangeListenTo: [
                                                          "dns",
                                                          ...IPV4_FIELDS,
                                                          ...IPV6_FIELDS.slice(1),
                                                      ],
                                                      onChange: ({ value, fieldApi }) => {
                                                          if (
                                                              fieldApi.form.getFieldValue("dns") !==
                                                              GlobalDnsType.Custom
                                                          )
                                                              return undefined;
                                                          const allEmpty = [
                                                              value,
                                                              ...IPV4_FIELDS.map((n) => fieldApi.form.getFieldValue(n)),
                                                              ...IPV6_FIELDS.slice(1).map((n) =>
                                                                  fieldApi.form.getFieldValue(n),
                                                              ),
                                                          ].every((v) => v === "");
                                                          return allEmpty
                                                              ? t("error.one-ip-should-provided")
                                                              : undefined;
                                                      },
                                                  }
                                                : undefined
                                        }
                                    >
                                        {(fieldApi) => (
                                            <Field>
                                                <Input
                                                    placeholder={"2001:db8::1"}
                                                    value={fieldApi.state.value}
                                                    onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                    invalid={fieldApi.state.meta.errors.length > 0}
                                                />
                                                {fieldApi.state.meta.errors.map((err) => (
                                                    <ErrorMessage key={err}>{err}</ErrorMessage>
                                                ))}
                                            </Field>
                                        )}
                                    </form.Field>
                                ))}
                            </div>
                        </Fieldset>
                    </div>
                </>
            )}

            <Divider />
            <div className={"flex justify-end gap-3"}>
                <Button plain={true} onClick={() => form.reset()} disabled={!isDirty}>
                    {t("button.reset")}
                </Button>
                <PrimaryButton type={"submit"} disabled={!isDirty}>
                    {tg("button.save")}
                </PrimaryButton>
            </div>
        </Form>
    );
}

/**
 * Properties for {@link PresetRadio}.
 */
type PresetRadioProps = {
    /** The provider value to set on the form when this radio is chosen. */
    value: GlobalDnsType;
    /** Localised provider label (e.g. "Cloudflare DNS"). */
    label: string;
    /** Public IPs to display under the radio. */
    preset: ProviderResolverPreset;
};

/**
 * One radio entry in the global DNS provider picker, with the provider's IPs shown beneath
 * the label so the user can see what they're selecting.
 *
 * @param props see {@link PresetRadioProps}
 * @returns the rendered radio field
 */
function PresetRadio(props: PresetRadioProps) {
    const { value, label, preset } = props;
    return (
        <RadioField>
            <Radio value={value} />
            <Label>{label}</Label>
            <Description>
                <span className={"block font-mono text-xs/4"}>{preset.ipv4.join(" · ")}</span>
                <span className={"block font-mono text-xs/4"}>{preset.ipv6.join(" · ")}</span>
            </Description>
        </RadioField>
    );
}

export const Route = createFileRoute("/_admin/a/dns/_dns/global")({
    component: GlobalDnsOverview,
    loader: async () => await ApiInternal.dns.get(),
});
